import { useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Pin, PinOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Returns the default landing tab saved on the user's profile
 * (or null if none set). Use `defaultTab ?? fallback` when initializing
 * activeTab state in a dashboard.
 */
export function useDefaultLandingTab(allowedKeys) {
  const { user } = useAuth();
  const raw = user && typeof user === "object" ? user.ui_preferences?.default_landing_tab : null;
  return useMemo(() => {
    if (!raw) return null;
    if (allowedKeys && !allowedKeys.includes(raw)) return null;
    return raw;
  }, [raw, allowedKeys]);
}

/**
 * Persist the given tab key as the user's new default landing tab. Pass
 * `null` to clear. Silent on error — UI preference, not critical.
 */
export async function saveDefaultLandingTab(tabKey) {
  try {
    await API.put("/profile/ui-preferences", { default_landing_tab: tabKey });
    return true;
  } catch {
    return false;
  }
}

/**
 * Small inline pin button. When the current tab (activeKey) is the user's
 * default, it shows filled with "Default" tooltip. When not, it's an outline
 * button "Pin as default". Clicking toggles the state.
 */
export default function PinTabButton({ activeKey, className = "" }) {
  const { user, checkAuth } = useAuth();
  const current = user?.ui_preferences?.default_landing_tab || null;
  const isDefault = current === activeKey;

  const onClick = useCallback(async (e) => {
    e.stopPropagation();
    const next = isDefault ? null : activeKey;
    const ok = await saveDefaultLandingTab(next);
    if (!ok) {
      toast.error("Couldn't save preference");
      return;
    }
    // Refresh user in AuthContext so the pin state updates immediately
    if (typeof checkAuth === "function") checkAuth();
    toast.success(next ? "Set as your default landing tab" : "Default tab cleared");
  }, [isDefault, activeKey, checkAuth]);

  const Icon = isDefault ? Pin : PinOff;
  // Rendered as <span role="button"> so this interactive element can be
  // safely nested inside parent nav <button>s (sidebar tabs) without
  // producing the "button nested in button" React hydration warning.
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); }
  };
  return (
    <span
      onClick={onClick}
      onKeyDown={handleKey}
      role="button"
      tabIndex={0}
      title={isDefault ? "This is your default landing tab — click to unpin" : "Pin as default landing tab"}
      aria-label={isDefault ? "Unpin default tab" : "Pin default tab"}
      aria-pressed={isDefault}
      data-testid={`pin-tab-${activeKey}`}
      data-pinned={isDefault ? "true" : "false"}
      className={`inline-flex items-center justify-center p-1 rounded cursor-pointer transition-colors ${
        isDefault
          ? "text-amber-500 dark:text-amber-400 hover:text-amber-600 dark:hover:text-amber-300"
          : "text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 opacity-0 group-hover:opacity-100"
      } ${className}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </span>
  );
}
